import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  getRangeVersion,
  getTeamRangesData,
  getTrackingData,
  patchRangeUpdate,
  postRangeReview,
  postRangeUpdate,
  sendRangeUpdate,
  setRangeVisualization
} from 'services/backoffice'
import {
  PatchRangeBody,
  PostRangeBody,
  RangeReviewBody,
  RangeVersion,
  TeamRange,
  TrackingData
} from 'services/backoffice/interfaces'
import { Pagination } from 'store/InventoryContext/Types/InventoryType'
import { AppThunk } from 'store/store'

type InitialStateType = {
  loadingData: boolean
  teamRanges: TeamRange[]
  rangeVersion: RangeVersion
  loadingTracking: boolean
  trackingData: TrackingData[]
  trackingPag: Pagination
}

const initialState: InitialStateType = {
  loadingData: true,
  teamRanges: [],
  rangeVersion: {} as RangeVersion,
  loadingTracking: true,
  trackingData: [],
  trackingPag: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0
  }
}

const backoffice = createSlice({
  name: 'backoffice',
  initialState,
  reducers: {
    setLoadingData(state, action: PayloadAction<boolean>) {
      state.loadingData = action.payload
    },
    setTeamRanges(state, action: PayloadAction<TeamRange[]>) {
      state.teamRanges = action.payload
    },
    setRangeVersion(state, action: PayloadAction<RangeVersion>) {
      state.rangeVersion = action.payload
    },
    setLoadingTracking(state, action: PayloadAction<boolean>) {
      state.loadingTracking = action.payload
    },
    setTrackingData(state, action: PayloadAction<TrackingData[]>) {
      state.trackingData = action.payload
    },
    setTrackingPage(state, action: PayloadAction<number>) {
      state.trackingPag.currentPage = action.payload
    },
    setTrackingPagination(state, action: PayloadAction<Pagination>) {
      state.trackingPag = action.payload
    },
    resetTrackingPagination(state) {
      state.trackingPag = {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0
      }
    }
  }
})

export const {
  setLoadingData,
  setTeamRanges,
  setRangeVersion,
  setLoadingTracking,
  setTrackingData,
  setTrackingPage,
  setTrackingPagination,
  resetTrackingPagination
} = backoffice.actions

export const backofficeContext = backoffice.reducer

export const getTeamRanges =
  (team: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingData(true))
    try {
      const ranges = await getTeamRangesData(team)
      if (ranges && ranges.length) {
        dispatch(setTeamRanges(ranges))
        const version = await getRangeVersion(team)
        dispatch(setRangeVersion(version))
      } else {
        dispatch(setTeamRanges([]))
        dispatch(setRangeVersion({} as RangeVersion))
      }
    } catch (error) {
      toast.error('Erro ao carregar configurações de alçadas.')
    } finally {
      dispatch(setLoadingData(false))
    }
  }

export const dispatchRangeUpdate =
  (payload: PostRangeBody, currentOffice: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingData(true))
    try {
      await postRangeUpdate(payload)
    } catch (error) {
      toast.error('Erro ao atualizar configurações de alçadas.')
    } finally {
      dispatch(getTeamRanges(currentOffice))
    }
  }

export const dispatchRangeUpdatePatch =
  (payload: PatchRangeBody, currentOffice: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingData(true))
    try {
      await patchRangeUpdate(payload)
    } catch (error) {
      toast.error('Erro ao atualizar configurações de alçadas.')
    } finally {
      dispatch(getTeamRanges(currentOffice))
    }
  }

export const dispatchSendRange =
  (version: number, currentOffice: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingData(true))
    try {
      await sendRangeUpdate(version)
      toast.success('Solicitação de aprovação enviada com sucesso!')
    } catch (error) {
      toast.error('Erro ao enviar alterações de alçada para aprovação.')
    } finally {
      dispatch(getTeamRanges(currentOffice))
    }
  }

export const dispatchRangeVisualization =
  (version: number, currentOffice: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingData(true))
    try {
      await setRangeVisualization(version)
    } catch (error) {
      toast.error('Erro ao visualizar configurações de alçadas.')
    } finally {
      dispatch(getTeamRanges(currentOffice))
    }
  }

export const dispatchRangeReview =
  (payload: RangeReviewBody, currentOffice: string): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingData(true))
    try {
      await postRangeReview(payload)
      toast.success(
        `Política de alçadas ${payload.aprovado ? 'aprovada' : 'reprovada'} com sucesso!`
      )
    } catch (error) {
      toast.error('Erro ao revisar configurações de alçadas.')
    } finally {
      dispatch(getTeamRanges(currentOffice))
    }
  }

export const getTracking =
  (page: number, pageSize: number): AppThunk =>
  async (dispatch) => {
    dispatch(setLoadingTracking(true))
    try {
      const trackingData = await getTrackingData({ Pagina: page - 1, Linhas: pageSize })
      dispatch(setTrackingData(trackingData))
      if (trackingData.length) {
        dispatch(
          setTrackingPagination({
            pageSize,
            currentPage: page,
            totalItems: trackingData[0].TotalRegistros,
            totalPages: Math.ceil(trackingData[0].TotalRegistros / 10)
          })
        )
      }
    } catch (error) {
      toast.error('Erro ao carregar tracking de alçadas.')
    } finally {
      dispatch(setLoadingTracking(false))
    }
  }
